import { FC, useState } from "react"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

interface LabeledInputProps {
  label: string
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  type?: string
  hint?: string
  autoComplete?: string
  /** Show an eye-toggle button to reveal/hide the value (use instead of type="password") */
  revealable?: boolean
  /** Validation message; marks the field invalid and replaces the hint. */
  error?: string
}

export const LabeledInput: FC<LabeledInputProps> = ({
  label,
  id,
  value,
  onChange,
  disabled,
  placeholder,
  type = "text",
  hint,
  autoComplete = "off",
  revealable,
  error
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [revealed, setRevealed] = useState(false)
  // readOnly on mount prevents Firefox/Chrome from triggering the password manager
  // for revealable fields. Removed on first user interaction.
  const [interacted, setInteracted] = useState(false)
  const inputType = revealable ? (revealed ? "text" : "password") : type

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className={clsx("text-sm font-medium", error ? "text-error" : "text-gray-600 dark:text-gray-300")}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onMouseDown={() => setInteracted(true)}
          onFocus={() => setInteracted(true)}
          readOnly={revealable && !interacted}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          className={clsx(
            "input input-bordered w-full px-3 py-2 text-sm rounded-lg bg-white text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(169,78,206,0.35)] dark:bg-[#0C0C1A] dark:text-white dark:placeholder:text-gray-500 disabled:opacity-60 disabled:cursor-not-allowed pr-9",
            error ? "input-error border-error" : "border-black/10 dark:border-white/10"
          )}
        />
        {revealable && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setRevealed((r) => !r)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            aria-label={revealed ? t("common.hideValue") : t("common.showValue")}
          >
            {revealed ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
          </button>
        )}
      </div>
      {error ? (
        <p className="text-xs text-error">{error}</p>
      ) : (
        hint && <p className="text-xs text-gray-400 dark:text-gray-500">{hint}</p>
      )}
    </div>
  )
}
